// Using CSS-based scaling (similar to NotePadModal) so native scrollbars appear
import { Transition } from "@headlessui/react";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { XMarkIcon, MagnifyingGlassPlusIcon } from "@heroicons/react/24/outline";
import Draggable from "react-draggable";
import { ResizableBox } from "react-resizable";
import "react-resizable/css/styles.css";
import { calculateResizeBounds } from "../../../../helpers/functions";

/** Same as close button: padding, icon size, colors, hover. */
const MODAL_ICON_BUTTON_CLASS =
  "group flex shrink-0 items-center justify-center p-2 transition-all rounded-full cursor-pointer bg-blue-200 hover:bg-REELTeal hover:text-white text-teal-300";

const ZoomControlButton = ({ onClick, icon: Icon, label, className = "" }) => (
  <button
    type="button"
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    className={`${MODAL_ICON_BUTTON_CLASS} ${className}`}
    title={label}
  >
    {Icon ? <Icon className="h-5 w-5" /> : <span className="text-sm">{label}</span>}
  </button>
);

const MAX_SCALE = 3;
const INITIAL_MAX_WIDTH = 640;
const INITIAL_HEIGHT = 900;

const computeFitDimensions = (naturalW, naturalH) => {
  if (!naturalW || !naturalH) {
    return { width: INITIAL_MAX_WIDTH, height: INITIAL_HEIGHT };
  }
  const maxWidth = Math.min(1400, window.innerWidth - 40);
  const maxHeight = window.innerHeight - 80;
  const targetW = Math.min(naturalW, INITIAL_MAX_WIDTH, maxWidth);
  const aspectH = Math.round((targetW / naturalW) * naturalH) || naturalH;
  const targetH = Math.min(aspectH, maxHeight);
  return { width: targetW, height: targetH };
};

const zoomControls = [
  { icon: MagnifyingGlassPlusIcon, label: "Zoom In", action: "zoomIn" },
  {
    icon: ({ className }) => (
      <img
        src="/icons/revert-icon.svg"
        alt="Revert"
        className={`${className} !h-5 !w-5 shrink-0 group-hover:brightness-0 group-hover:invert`}
      />
    ),
    label: "Reset",
    action: "resetTransform",
  },
  ];

const TreatmentModal = ({ open = false, onCross = () => {} }) => {
  const [container, setContainer] = useState(null);
  const contentRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const headerRef = useRef(null);
  const imageRef = useRef(null);
  const hasInitializedDimensions = useRef(false);
  const [dimensions, setDimensions] = useState({ width: INITIAL_MAX_WIDTH, height: INITIAL_HEIGHT });
  const [maxConstraints, setMaxConstraints] = useState([1400, 1000]);
  const [minConstraints, setMinConstraints] = useState([320, 240]);
  const [portalKey, setPortalKey] = useState(0);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [scrollEnabled, setScrollEnabled] = useState(false);
  const [scale, setScale] = useState(1);
  const [naturalImageWidth, setNaturalImageWidth] = useState(null);
  const [naturalImageHeight, setNaturalImageHeight] = useState(null);

  // Mount on document.body so the overlay is not inside the flex/scroll layout (prevents the whole app shifting when resizing).
  useEffect(() => {
    setContainer(document.body);
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      hasInitializedDimensions.current = false;
      return;
    }

    if (contentRef.current && !hasInitializedDimensions.current) {
      const el = contentRef.current;
      const originalWidth = el.style.width;
      el.style.width = "auto";
      const naturalWidth = el.offsetWidth;
      el.style.width = originalWidth;
      const height = el.offsetHeight;
      setDimensions({ width: Math.min(naturalWidth, INITIAL_MAX_WIDTH), height });
      hasInitializedDimensions.current = true;
    }
  }, [open]);

  // Initialize modal size from image natural size and constrain it to the viewport
  const handleImageLoad = useCallback(() => {
    const img = imageRef.current;
    if (!img) return;

    const naturalW = img.naturalWidth || img.width;
    const naturalH = img.naturalHeight || img.height;
    setNaturalImageWidth(naturalW || null);
    setNaturalImageHeight(naturalH || null);

    if (hasInitializedDimensions.current) return;

    setDimensions(computeFitDimensions(naturalW, naturalH));
    hasInitializedDimensions.current = true;
  }, []);
  useEffect(() => {
    const updateConstraints = () => {
      const maxWidth = Math.min(1400, window.innerWidth - 40);
      const maxHeight = window.innerHeight - 80;
      const computedMinWidth = Math.max(320, Math.min(600, Math.floor(window.innerWidth * 0.3)));
      const computedMinHeight = Math.max(240, Math.floor(window.innerHeight * 0.2));
      setMaxConstraints([maxWidth, maxHeight]);
      setMinConstraints([computedMinWidth, computedMinHeight]);
      setDimensions((prev) => ({
        width: Math.min(Math.max(prev.width, computedMinWidth), maxWidth),
        height: Math.min(Math.max(prev.height, computedMinHeight), maxHeight),
      }));
    };

    updateConstraints();
    window.addEventListener("resize", updateConstraints);
    return () => window.removeEventListener("resize", updateConstraints);
  }, []);

  useEffect(() => {
    if (!open) {
      setDimensions({ width: INITIAL_MAX_WIDTH, height: INITIAL_HEIGHT });
      setPosition({ x: 0, y: 0 });
      setPortalKey((k) => k + 1);
      hasInitializedDimensions.current = false;
    }
  }, [open]);

  // Lock scroll on both roots so resize/drag does not move the shell or show stray scrollbars.
  useEffect(() => {
    if (!open) return;
    const prevBody = document.body.style.overflow;
    const prevHtml = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevBody;
      document.documentElement.style.overflow = prevHtml;
    };
  }, [open]);

  // During resize: update size only. Position correction runs once on stop (avoids whole-UI “jumping”).
  const handleResize = useCallback(
    (e, { size }) => {
      const constrainedWidth = Math.min(size.width, maxConstraints[0]);
      const constrainedHeight = Math.min(size.height, maxConstraints[1]);
      setDimensions({ width: constrainedWidth, height: constrainedHeight });
    },
    [maxConstraints]
  );

  const handleResizeStop = useCallback(
    (e, { size }) => {
      const constrainedWidth = Math.min(size.width, maxConstraints[0]);
      const constrainedHeight = Math.min(size.height, maxConstraints[1]);
      setDimensions({ width: constrainedWidth, height: constrainedHeight });
      setScrollEnabled(true);
      setPosition((prev) => {
        const { newPosition } = calculateResizeBounds({
          sizeWidth: constrainedWidth,
          sizeHeight: constrainedHeight,
          maxConstraints,
          currentPosition: prev,
          margin: 20,
          topMargin: 60,
        });
        if (newPosition.x !== prev.x || newPosition.y !== prev.y) return newPosition;
        return prev;
      });
    },
    [maxConstraints]
  );

  // When popup opens or re-opens, start with no scrolling
  useEffect(() => {
    if (open) {
      setScrollEnabled(false);
    }
  }, [open]);

  // Ensure native scrolling works when zoomed in
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleWheel = (e) => {
      if (scrollEnabled) e.stopPropagation();
    };

    scrollContainer.addEventListener("wheel", handleWheel, { passive: false, capture: true });
    return () => scrollContainer.removeEventListener("wheel", handleWheel, { capture: true });
  }, [scrollEnabled]);

  // Update scroll container max-height to fit within the current resizable box
  useEffect(() => {
    const scroll = scrollContainerRef.current;
    const header = headerRef.current;
    if (!scroll) return;

    const headerH = header ? header.offsetHeight : 0;
    const buffer = 48; // leave some breathing room for paddings/controls
    const maxH = Math.max(0, dimensions.height - headerH - buffer);
    scroll.style.maxHeight = `${maxH}px`;
  }, [dimensions, open]);

  if (!container) return null;

  return createPortal(
    <Transition
      show={open}
      enter="transition-opacity duration-300"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="transition-opacity duration-300"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div className="pointer-events-auto fixed inset-0 z-[1000] flex max-h-[100dvh] max-w-[100vw] items-center justify-center overflow-hidden overscroll-none">
        <div
          className="pointer-events-auto relative box-border flex h-full w-full min-h-0 min-w-0 max-h-full max-w-full items-center justify-center p-2"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <Draggable
            handle=".resize-header"
            bounds="parent"
            cancel=".react-resizable-handle"
            position={position}
            onDrag={(e, data) => setPosition({ x: data.x, y: data.y })}
          >
            <ResizableBox
              key={portalKey}
              width={dimensions.width}
              height={dimensions.height}
              minConstraints={minConstraints}
              maxConstraints={maxConstraints}
              resizeHandles={["se", "s", "e", "sw", "nw", "n", "ne", "w"]}
              onResize={handleResize}
              onResizeStop={handleResizeStop}
              draggableOpts={{ enableUserSelectHack: false }}
            >
                <div
                  ref={contentRef}
                  className="bg-white dialog pointer-events-auto border border-gray-300 rounded shadow-md relative"
                >
                  <div className="flex flex-col overflow-hidden flex-1 min-h-0">
                    <div className="flex flex-col h-full">
                      <div className="dialog-header resize-header bg-gray-20 flex items-center gap-4 px-4 py-6 rounded-t-[4px] select-none cursor-move flex-shrink-0 relative">
                        {/* headerRef used to calculate available body height */}
                        <div ref={headerRef} className="min-w-0 flex-1">
                          <h3 className="text-xl font-bold text-gray-800">
                            Treatment Algorithm
                          </h3>
                        </div>
                        <div className="z-50 flex shrink-0 items-center gap-4 pointer-events-auto">
                          {zoomControls.map((control) => (
                            <ZoomControlButton
                              key={control.action}
                              onClick={() => {
                                if (control.action === 'zoomIn') {
                                  setScale(prev => Math.min(MAX_SCALE, +(prev + 0.25).toFixed(2)));
                                  setScrollEnabled(true);
                                } else if (control.action === 'resetTransform') {
                                  setScale(1);
                                  setScrollEnabled(false);
                                  if (naturalImageWidth && naturalImageHeight) {
                                    setDimensions(
                                      computeFitDimensions(naturalImageWidth, naturalImageHeight)
                                    );
                                  } else {
                                    setDimensions({
                                      width: INITIAL_MAX_WIDTH,
                                      height: INITIAL_HEIGHT,
                                    });
                                  }
                                  setPosition({ x: 0, y: 0 });
                                }
                              }}
                              icon={control.icon}
                              label={control.label}
                              className={control.className || ""}
                            />
                          ))}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onCross();
                            }}
                            className={MODAL_ICON_BUTTON_CLASS}
                          >
                            <span className="sr-only">Close panel</span>
                            <XMarkIcon
                              className="h-5 w-5"
                              strokeWidth="2"
                              aria-hidden="true"
                            />
                          </button>
                        </div>
                      </div>

                      <div 
                        ref={scrollContainerRef}
                        className={`dialog-body text-xl select-none flex-1 min-h-0 ${
                          scrollEnabled ? "body-scrollbar overflow-auto" : "overflow-hidden"
                        }`}
                        onWheel={(e) => {
                          if (scrollEnabled) {
                            // Allow native scrolling when zoomed in
                            e.stopPropagation();
                          }
                        }}
                      >
                        <div
                          className="relative min-w-0 w-full"
                          onWheel={(e) => {
                            if (scrollEnabled) {
                              e.stopPropagation();
                            }
                          }}
                        >
                          <div className="flex w-full min-w-0 justify-center">
                            <img
                              ref={imageRef}
                              onLoad={handleImageLoad}
                              src="/images/allergic-rhinitis-algo.png"
                              alt="Treatment algorithm"
                              draggable={false}
                              style={
                                scale !== 1 || scrollEnabled
                                  ? {
                                      width: `${scale * 100}%`,
                                      maxWidth: "none",
                                      height: "auto",
                                      objectFit: "contain",
                                      display: "block",
                                      marginLeft: 0,
                                      marginRight: 0,
                                    }
                                  : {
                                      width: "100%",
                                      maxWidth: naturalImageWidth
                                        ? `${naturalImageWidth}px`
                                        : "100%",
                                      height: "auto",
                                      objectFit: "contain",
                                      display: "block",
                                      maxHeight: "calc(100vh - 200px)",
                                    }
                              }
                              className={`shrink-0 ${scale === 1 && !scrollEnabled ? "mx-auto" : ""}`}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
            </ResizableBox>
          </Draggable>
        </div>
      </div>
    </Transition>,
    container
  );
};

export default TreatmentModal;
